import React, { useState, useRef } from "react";
import Layout from "../../components/layout/Layout";
import Loader from "../../components/Loader";
import Pagination from "../../components/pagination";
import useGet from "../../hooks/read/useGet";
import usePut from "../../hooks/usePut";
import { debounce } from "lodash";
import { PencilAltIcon } from "@heroicons/react/solid";
import EditSalaryStructureModal from "./EditSalaryStructureModal";
import toast from "react-hot-toast";

const SalaryStructure = () => {
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch list using useGet
  const {
    list: structureListReq,
    paramsObject,
    setGetListParams,
  } = useGet({
    url: "salary-structure/list",
    initialData: {
      page_number: 0,
      limit: 10,
      search_query: "",
    },
  });

  const structureList = structureListReq?.data?.data || [];
  const isLoading = structureListReq.isLoading;
  const paginationData = {
    page_no: structureListReq?.data?.page_no || 0,
    per_page: structureListReq?.data?.per_page || 10,
    total_count: structureListReq?.data?.total_count || 0,
  };

  const onPageChange = (selected) => {
    setGetListParams((prev) => ({
      ...prev,
      page_number: selected.selected,
    }));
  };

  // Status update mutation using usePut
  const { handleAuth: statusUpdateAuth } = usePut({
    url: "salary-structure/status_update",
    refreshUrl: "salary-structure/list",
    onSuccess: (res) => {
      toast.success(res?.data?.message || "Status updated successfully");
    },
    onError: (err) => {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to update status");
    },
  });

  // Modal state
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedStructure, setSelectedStructure] = useState(null);

  // Debounced search
  const debouncedSearch = useRef(
    debounce((val) => {
      setGetListParams((prev) => ({
        ...prev,
        search_query: val,
        page_number: 0,
      }));
    }, 350)
  ).current;

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleOpenEdit = (item) => {
    setSelectedStructure(item);
    setIsEditOpen(true);
  };

  const handleStatusToggle = (item) => {
    const id = item.salary_structure_id;
    const nextStatus = !item.show_status;

    statusUpdateAuth.mutate({
      salary_structure_id: id,
      show_status: nextStatus ? 1 : 0,
    });
  };

  return (
    <Layout>
      <div className="px-6 py-6 min-h-screen">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold roboto text-gray-800">
              Salary Structures
            </h1>
          </div>
        </div>

        {/* Filter / Search Bar */}
        <div className="mb-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="w-full md:w-80">
            <input
              type="search"
              placeholder="Search by structure name or slug..."
              value={searchQuery}
              onChange={handleSearch}
              className="w-full px-4 py-2 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[color:var(--color1)] text-sm shadow-sm"
            />
          </div>
        </div>

        {/* Loading Spinner */}
        {isLoading ? (
          <div className="grid place-items-center pt-32 pb-20">
            <Loader className="!text-color1 w-14 h-14" />
          </div>
        ) : (
          <>
            {/* Table or Empty State */}
            {structureList?.length === 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 p-12 text-center my-6">
                <p className="text-gray-600 text-lg font-medium">
                  No Salary Structures Found
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  {searchQuery
                    ? "Try adjusting your search criteria"
                    : "No salary structures currently available"}
                </p>
              </div>
            ) : (
              <div className="max-w-full overflow-x-auto bg-white rounded-lg border border-gray-200 shadow-sm">
                <table className="table-auto w-full text-sm">
                  <thead>
                    <tr className="bg-[color:var(--color1)] text-center text-white">
                      <th className="font-semibold py-3.5 px-3 w-[5%] border-l border-transparent">
                        Sr. No
                      </th>
                      <th className="font-semibold py-3.5 px-4 text-left w-[14%]">
                        Structure Name
                      </th>
                      <th className="font-semibold py-3.5 px-3 text-left w-[10%]">
                        Slug
                      </th>
                      <th className="font-semibold py-3.5 px-3 text-center w-[23%] whitespace-nowrap">
                        Display Period
                      </th>
                      <th className="font-semibold py-3.5 px-3 text-center w-[23%] whitespace-nowrap">
                        Effective Period
                      </th>
                      <th className="font-semibold py-3.5 px-2 text-center w-[9%] whitespace-nowrap">
                        Can Update
                      </th>
                      <th className="font-semibold py-3.5 px-3 text-center w-[8%] whitespace-nowrap">
                        Status
                      </th>
                      <th className="font-semibold py-3.5 px-4 text-center w-[8%] border-r border-transparent">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {structureList.map((item, index) => {
                      const {
                        salary_structure_id,
                        name,
                        slug,
                        show_from_date_formatted,
                        show_to_date_formatted,
                        from_date_formatted,
                        to_date_formatted,
                        can_update,
                        show_status,
                      } = item;

                      const serialNumber =
                        paramsObject.limit * parseInt(paramsObject.page_number) +
                        (index + 1);

                      const isActive = Boolean(show_status);

                      const displayPeriod =
                        show_from_date_formatted || show_to_date_formatted
                          ? `${show_from_date_formatted || "--"} to ${show_to_date_formatted || "--"}`
                          : "--";

                      const effectivePeriod =
                        from_date_formatted || to_date_formatted
                          ? `${from_date_formatted || "--"} to ${to_date_formatted || "--"}`
                          : "--";

                      return (
                        <tr
                          key={salary_structure_id || index}
                          className="hover:bg-gray-50 transition border-b border-gray-200"
                        >
                          <td className="text-center font-medium py-3.5 px-2 bg-[#F3F6FF] border-r border-[#E8E8E8] text-gray-700">
                            {serialNumber}
                          </td>
                          <td className="py-3.5 px-4 font-semibold text-gray-800 text-left">
                            {name}
                          </td>
                          <td className="py-3.5 px-3 text-left font-mono text-xs text-gray-600">
                            {slug || "---"}
                          </td>
                          <td className="text-center py-3.5 px-3 text-gray-700 text-xs whitespace-nowrap font-medium">
                            {displayPeriod}
                          </td>
                          <td className="text-center py-3.5 px-3 text-gray-700 text-xs whitespace-nowrap font-medium">
                            {effectivePeriod}
                          </td>
                          <td className="text-center py-3.5 px-2">
                            {can_update ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                                Yes
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                                No
                              </span>
                            )}
                          </td>
                          <td className="text-center py-3.5 px-3">
                            <button
                              type="button"
                              onClick={() => handleStatusToggle(item)}
                              disabled={statusUpdateAuth.isLoading}
                              title="Click to toggle status"
                              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                isActive ? "bg-emerald-600" : "bg-gray-300"
                              } ${
                                statusUpdateAuth.isLoading
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              }`}
                            >
                              <span
                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  isActive ? "translate-x-5" : "translate-x-0"
                                }`}
                              />
                            </button>
                          </td>
                          <td className="text-center py-3.5 px-4">
                            <div className="flex items-center justify-center">
                              {/* Edit Action */}
                              <button
                                type="button"
                                onClick={() => handleOpenEdit(item)}
                                title="Edit Structure"
                                className="p-1.5 rounded text-gray-600 hover:text-emerald-600 hover:bg-emerald-50 transition"
                              >
                                <PencilAltIcon className="w-5 h-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {structureList?.length > 0 && (
              <div className="grid place-items-center mt-6 mb-8">
                <Pagination
                  currentPage={paginationData.page_no}
                  lengthofItems={paginationData.total_count}
                  limit={paginationData.per_page}
                  onPageChange={onPageChange}
                />
              </div>
            )}
          </>
        )}

        {/* Edit Modal */}
        {isEditOpen && (
          <EditSalaryStructureModal
            isOpen={isEditOpen}
            onClose={() => {
              setIsEditOpen(false);
              setSelectedStructure(null);
            }}
            salaryStructureId={selectedStructure?.salary_structure_id}
            data={selectedStructure}
          />
        )}
      </div>
    </Layout>
  );
};

export default SalaryStructure;
